import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import Slider from "react-slick";
import CardProdotto from "./card-prodotto";
import useProductPrices from "../hooks/use-product-prices";
import CompareContext from "../context/compare";

const settings = {
  dots: false,
  infinite: false,
  autoplay: false,
  arrows: true,
  swipe: true,
  slidesToShow: 3,
  responsive: [
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: 576,
      settings: {
        slidesToShow: 1,
        centerMode: true,
        centerPadding: "0px",
      },
    },
  ],
};

const SliderRelated = ({
  data: prodotti,
  config,
  labelManagerLabels,
  compareProducts,
  ecommerceActive,
  location,
  productNote,
  isCartModal,
  madeProductLineId,
  isAcCompare,
  onHide,
  setIsProducts,
}) => {
  const [prodottiItems, setProdottiItems] = useState([]);
  const { productIds: compareIds, toggleProduct } = useContext(CompareContext);
  const onToggleProduct = useCallback((e) => toggleProduct(e.currentTarget.name), [toggleProduct]);
  const productIds = useMemo(() => prodotti.map((item) => item.key_prod), [prodotti]);
  const priceInfo = useProductPrices(productIds, prodotti);
  useEffect(() => {
    if (ecommerceActive && priceInfo) {
      // Enrich with Ecommerce info
      const productsWithPriceInfo = prodotti.map((prodotto) => {
        const itemData = priceInfo.get(prodotto.key_prod);
        return {
          ...prodotto,
          msrpPrice: itemData?.msrpPrice || null,
          sellingPrice: itemData?.sellingPrice || null,
          omnibusPrice: itemData?.omnibusPrice || null,
          availableForSale: itemData?.availableForSale,
          shopLink: itemData?.shopLink || prodotto?.shopLink || null,
          special_from_date: itemData?.special_from_date,
          special_to_date: itemData?.special_to_date,
          contributoRaee: itemData?.contributo_raee ? itemData.contributo_raee : null,
        };
      });

      // Sort by in-stock first, higher price first
      productsWithPriceInfo.sort((a, b) => {
        const aCurrentPrice = a.sellingPrice?.value;
        const bCurrentPrice = b.sellingPrice?.value;
        const aInStock = !!a.availableForSale;
        const bInStock = !!b.availableForSale;

        if (aInStock === bInStock && !isAcCompare) {
          return aCurrentPrice > bCurrentPrice ? -1 : 1;
        }
        if (aInStock === bInStock && isAcCompare) {
          return aCurrentPrice === bCurrentPrice ? 0 : aCurrentPrice > bCurrentPrice ? -1 : 1;
        }
        return aInStock ? -1 : 1;
      });
      // Cap result at 9
      if (isAcCompare) {
        setProdottiItems(productsWithPriceInfo);
      } else {
        setProdottiItems(productsWithPriceInfo.slice(0, 9));
      }
    } else if (!ecommerceActive) {
      setProdottiItems(prodotti);
    }
  }, [ecommerceActive, priceInfo, prodotti, isAcCompare]);

  const hasCompare = useMemo(() => {
    return prodottiItems.every((item) =>
      compareProducts.some((product) => product.key_prod === item.key_prod)
    );
  }, [prodottiItems, compareProducts]);

  const prodottiItemsWithPrice = useMemo(() => {
    return prodottiItems?.filter((item) => item?.sellingPrice?.value);
  }, [prodottiItems]);

  useEffect(() => {
    if (isCartModal) {
      setIsProducts(prodottiItems?.filter((item) => item?.sellingPrice?.value)?.length > 0);
    }
  }, [isCartModal, prodottiItems, setIsProducts]);

  const productsFiltered = useMemo(() => {
    return isCartModal ? prodottiItemsWithPrice?.slice(0, 15) : prodottiItems;
  }, [isCartModal, prodottiItemsWithPrice, prodottiItems]);

  const prodottiItemsSorted = useMemo(() => {
    if (!isAcCompare) {
      return productsFiltered;
    } else {
      const productsBySkuWithPrice = productsFiltered?.filter(
        (product) => product?.sellingPrice?.value && product?.isBySku
      );
      const productsByLineWithPrice = productsFiltered?.filter(
        (product) => product?.sellingPrice?.value && !product?.isBySku
      );
      const productsBySkuWithoutPrice = productsFiltered?.filter(
        (product) => !product?.sellingPrice?.value && product?.isBySku
      );
      const productsByLineWithoutPrice = productsFiltered?.filter(
        (product) => !product?.sellingPrice?.value && !product?.isBySku
      );
      return productsBySkuWithPrice?.concat(
        productsByLineWithPrice,
        productsBySkuWithoutPrice,
        productsByLineWithoutPrice
      );
    }
  }, [productsFiltered, isAcCompare]);

  return (
    <>
      {prodottiItemsSorted?.length ? (
        <section className="section prodotti">
          <div className="wrapper-slider">
            <div className="container-fluid">
              <div className="row">
                {isCartModal && prodottiItemsSorted?.length ? (
                  <div className="col-12">
                    <h2 className="section__title mb-4 mt-4">
                      {labelManagerLabels.cartModalRelatedProducts}
                    </h2>
                  </div>
                ) : isAcCompare ? (
                  <div className="col-12">
                    <h2 className="section__title mb-4">
                      {labelManagerLabels.accessoriesConsumables}
                    </h2>
                  </div>
                ) : !isAcCompare && !isCartModal ? (
                  <div className="col-12">
                    <h2 className="section__title mb-4">{labelManagerLabels.relatedProducts}</h2>
                  </div>
                ) : null}
              </div>
              {prodottiItemsSorted.length ? (
                <div className="row">
                  <div className="col-12 px-0 px-md-3">
                    <Slider {...settings} className="prodotti__slider">
                      {prodottiItemsSorted.map((item, i) => (
                        <div key={i}>
                          <CardProdotto
                            data={item}
                            isSliderProduct={true}
                            compare={hasCompare}
                            productIds={hasCompare && compareIds}
                            toggleProduct={onToggleProduct}
                            config={config}
                            origin={
                              isAcCompare
                                ? "Aec product - PDP"
                                : isCartModal
                                ? "Cart Modal"
                                : "Related product - PDP"
                            }
                            labelManagerLabels={labelManagerLabels}
                            location={location}
                            addCart={true}
                            productNote={productNote}
                            compareProducts={compareProducts}
                            madeProductLineId={madeProductLineId}
                            onHide={onHide}
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};

export default SliderRelated;
